import React, { useState } from 'react';
import { Play, Youtube } from 'lucide-react';

/**
 * Professional video section for practice area pages
 * Displays 1-4 videos in a clean grid layout with play functionality
 */
const PageVideoSection = ({ 
  videos = [], 
  title = "Video Resources",
  subtitle = "Learn more about your rights and options",
  theme = "rose" // "rose", "teal", "slate"
}) => {
  const [activeVideo, setActiveVideo] = useState(null);

  if (!videos || videos.length === 0) return null;

  const themeColors = {
    rose: {
      badge: "bg-coral-100 text-primary",
      button: "bg-primary hover:bg-coral-600",
      accent: "text-primary"
    },
    teal: {
      badge: "bg-teal-100 text-teal-700",
      button: "bg-teal-600 hover:bg-teal-700",
      accent: "text-teal-600"
    },
    slate: {
      badge: "bg-slate-100 text-slate-700",
      button: "bg-slate-700 hover:bg-slate-800",
      accent: "text-slate-700"
    }
  };

  const colors = themeColors[theme] || themeColors.rose;

  // Determine grid columns based on video count
  const getGridCols = () => {
    if (videos.length === 1) return "grid-cols-1 max-w-2xl mx-auto";
    if (videos.length === 2) return "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto";
    if (videos.length === 3) return "grid-cols-1 md:grid-cols-3";
    return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
  };

  return (
    <section className="py-16 bg-slate-50" data-testid="page-video-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 ${colors.badge} px-4 py-2 rounded-full text-sm font-medium mb-4`}>
            <Youtube className="w-4 h-4" />
            {title}
          </div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Active Video Player */}
        {activeVideo && (
          <div className="mb-10">
            <div className="relative aspect-video max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
                title="Video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
            <button 
              onClick={() => setActiveVideo(null)}
              className="mt-4 mx-auto block text-slate-500 hover:text-slate-700 text-sm transition-colors"
            >
              Close video
            </button>
          </div>
        )}

        {/* Video Grid */}
        <div className={`grid gap-6 ${getGridCols()}`}>
          {videos.map((video) => (
            <div 
              key={video.id}
              className="group cursor-pointer"
              onClick={() => setActiveVideo(video.id)}
              data-testid={`page-video-${video.id}`}
            >
              <div className="relative aspect-video rounded-xl overflow-hidden shadow-md mb-3 bg-slate-200">
                <img 
                  src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/70 transition-colors flex items-center justify-center">
                  <div className={`w-14 h-14 ${colors.button} rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
                    <Play className="w-6 h-6 text-white ml-1" fill="white" />
                  </div>
                </div>
                {video.duration && (
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                )}
                {activeVideo === video.id && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                    Now Playing
                  </div>
                )}
              </div>
              <h3 className={`font-heading text-base font-semibold text-secondary group-hover:${colors.accent} transition-colors line-clamp-2`}>
                {video.title}
              </h3>
              {video.description && (
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                  {video.description}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* YouTube Channel Link */}
        <div className="text-center mt-10">
          <a 
            href="https://www.youtube.com/@amycbrownlaw"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-red-600 text-sm font-medium transition-colors"
          >
            <Youtube className="w-4 h-4" />
            View all videos on YouTube
          </a>
        </div>
      </div>
    </section>
  );
};

export default PageVideoSection;
